import {Injectable}                           from '@angular/core';
import {Create, ObjectFactory}                from 'ts-tooling';
import {ApStatsType}                          from '../../../ap-interface';
import {IStatisticBatchItem, StatisticStore}  from '../../../stores/statistic/statistic.store';
import {ApDynGridColumnConfigBase}            from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPropertyColumnConfig}        from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynGridGroupColumnConfig}           from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {GetRoundNumericPipe}                  from '../../../ap-utils';
import {GetRoundNumericPipeConfig}            from '../../../ap-utils/configs/get-round-numeric.pipe.config';
import {nameOf}                               from '../../../ap-core/utils/ap-name-of';
import {LanguageStore}                        from '../../../stores/translation/language.store';
import {SettingsStore}                        from '../../../stores/base-data/settings.store';
import {TranslationStore}                     from '../../../stores/translation/translation.store';
import {NFertilizationPlantProtectionService} from '../../../services/data/n-fertilization-plant-protection.service';
import {IFieldScanDataStatsTableItem}         from '../types/field-scan-data-table-stats.types';
import {
  IGenericTableStatsConfig
}                                             from '../../../statistics/components/charts/ap-generic-table-stats/ap-generic-table-stats.component';

@Injectable({providedIn: 'root'})
export class FieldScanDataTableStatisticService {
  private readonly MAX_STAT_ITEMS = 25;

  constructor(private statisticStore: StatisticStore,
              private translationStore: TranslationStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private roundNumericPipe: GetRoundNumericPipe,
              private nFertilizationPlantProtectionService: NFertilizationPlantProtectionService) {
  }

  /**
   * Builds and publishes the field-scan data table as a single GenericTableStats
   * batch item. The table is capped at {@link MAX_STAT_ITEMS} rows (followed by a
   * "+N more" row) and carries a sum/avg footer row, matching the former bespoke
   * ApFieldScanDataTableStatsComponent exactly.
   * @param title - Pre-translated header rendered above the table.
   * @param items - Field-scan rows to render.
   * @param emptyMessage - Message shown when there are no rows.
   * @public
   */
  public generateStats(title: string, items: IFieldScanDataStatsTableItem[], emptyMessage = ''): void {
    if (!items || items.length === 0) {
      this.statisticStore.setStatisticDataBatch([], title, emptyMessage);
      return;
    }
    const batchItem: IStatisticBatchItem<{
      columns: ApDynGridColumnConfigBase[];
      items: IFieldScanDataStatsTableItem[];
      config: IGenericTableStatsConfig;
    }> = {
      data: {
        columns: this._buildColumns(),
        items: this._buildRows(items),
        config: {renderMode: 'grid'}
      },
      type: ApStatsType.GenericTableStats,
      title
    };
    this.statisticStore.setStatisticDataBatch([batchItem], title, emptyMessage);
  }

  /**
   * Sorts, caps and footers the field-scan rows for display.
   * Preserves the original sort (case-insensitive by field name, null-safe), the
   * 25-row cap with a "+N more" placeholder row, and the area/min/max/weighted-avg
   * summary row appended at the end.
   * @param data - The unshaped field-scan rows.
   * @return The display-ready row list.
   * @private
   */
  private _buildRows(data: IFieldScanDataStatsTableItem[]): IFieldScanDataStatsTableItem[] {
    // Cannot use 'SortBy' here because it has problems if fieldname is undefined
    let statGridDataItems: IFieldScanDataStatsTableItem[] = ObjectFactory.Copy(data).sort((a, b) => {
      return (a.FieldName?.toLowerCase() < b.FieldName?.toLowerCase()) ? -1
        : ((b.FieldName?.toLowerCase() > a.FieldName?.toLowerCase()) ? 1 : 0);
    });
    if (statGridDataItems.length > this.MAX_STAT_ITEMS) {
      statGridDataItems = ObjectFactory.Copy(statGridDataItems.slice(0, this.MAX_STAT_ITEMS));
      statGridDataItems.push({
        FieldNumber: undefined,
        FieldName: this.translationStore.FindTranslationForSelectedLanguage('Nutrient__Param_ManyMore', [`${(data.length - this.MAX_STAT_ITEMS)}`]),
        FieldSubNumber: undefined,
        ScanDate: undefined,
        Area: undefined,
        ScanNUptakeMin: undefined,
        ScanNUptakeMax: undefined,
        ScanNUptakeAvg: undefined
      });
    }
    const filteredItemsBySensor = data.filter(item => Number.isFinite(item.ScanNUptakeAvg));
    const selectedFieldsAreaAndAverage = filteredItemsBySensor.map(item => ({
      Area: Create(item.Area, 0),
      Average: item.ScanNUptakeAvg,
      Factor: undefined
    }));
    const sumDataRow: IFieldScanDataStatsTableItem = {
      FieldNumber: undefined,
      FieldName: undefined,
      FieldSubNumber: undefined,
      ScanDate: undefined,
      Area: data.reduce((a, b) => a + Create(b?.Area, 0), 0),
      ScanNUptakeMin: filteredItemsBySensor.reduce((a, b) => (b.ScanNUptakeMin < a) || (a === 0 && b.ScanNUptakeMin > -1) ? b.ScanNUptakeMin : a, 0),
      ScanNUptakeMax: filteredItemsBySensor.reduce((a, b) => b.ScanNUptakeMax > a && b.ScanNUptakeMax > -1 ? b.ScanNUptakeMax : a, 0),
      ScanNUptakeAvg: this.nFertilizationPlantProtectionService.calculateAreaWeightedAverage(selectedFieldsAreaAndAverage)
    };
    statGridDataItems.push(sumDataRow);
    return statGridDataItems;
  }

  /**
   * Builds the grid column configuration for the field-scan table: field number,
   * sub-number, name, area, scan date and the grouped N-Uptake avg/min/max columns.
   * @return The column configuration list.
   * @private
   */
  private _buildColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: nameOf<IFieldScanDataStatsTableItem>('FieldNumber'),
        width: 60,
        filterable: false,
        sortable: false
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini_Subnumber',
        field: nameOf<IFieldScanDataStatsTableItem>('FieldSubNumber'),
        width: 60,
        filterable: false,
        sortable: false
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Name',
        field: nameOf<IFieldScanDataStatsTableItem>('FieldName'),
        width: 150,
        filterable: false,
        sortable: false
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Area',
        field: nameOf<IFieldScanDataStatsTableItem>('Area'),
        width: 60,
        filterable: false,
        sortable: false,
        cssClass: 'center',
        headerStyle: {'text-align': 'center', padding: '6px 6px 6px 5px'},
        tooltip: (dataItem) => dataItem['Area'],
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [
            this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint ?? 0,
          ]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Date',
        field: nameOf<IFieldScanDataStatsTableItem>('ScanDate'),
        format: this.languageStore.CurrentDateFormat,
        width: 70,
        cssClass: 'center',
        headerStyle: {'text-align': 'center', padding: '6px 6px 6px 5px'},
        filterable: false,
        sortable: false,
      }),
      new ApDynGridGroupColumnConfig({
        title: 'N__Uptake',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__AvgShort',
            field: nameOf<IFieldScanDataStatsTableItem>('ScanNUptakeAvg'),
            cssClass: 'center',
            headerStyle: {'text-align': 'center', padding: '6px 6px 6px 5px'},
            width: 105,
            filterable: false,
            sortable: false,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [
                this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint ?? 0,
                GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
              ]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: nameOf<IFieldScanDataStatsTableItem>('ScanNUptakeMin'),
            cssClass: 'center',
            headerStyle: {'text-align': 'center', padding: '6px 6px 6px 5px'},
            width: 105,
            filterable: false,
            sortable: false,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [
                this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint ?? 0,
                GetRoundNumericPipeConfig.NullToEmptyReplaceConfig
              ]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: nameOf<IFieldScanDataStatsTableItem>('ScanNUptakeMax'),
            cssClass: 'center',
            headerStyle: {'text-align': 'center', padding: '6px 6px 6px 5px'},
            width: 105,
            filterable: false,
            sortable: false,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [
                this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint ?? 0,
                GetRoundNumericPipeConfig.NullToEmptyReplaceConfig
              ]
            }]
          })
        ]
      })
    ];
  }
}
